import { useRef, useState } from 'react'
import { requestCommerceManagement } from '../management-api.js'
import { operationKey, productForm, productPayload } from '../management-model.js'

const button = 'rounded border border-slate-300 px-3 py-2 text-sm disabled:opacity-50 dark:border-slate-600'
const input = 'mt-1 block w-full rounded border border-slate-300 bg-transparent p-2 dark:border-slate-600'

export default function ProductActions({ product, lang = 'zh', operationKeys, onBusyChange, onSuccess }) {
  const [form, setForm] = useState(null)
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const lock = useRef(false)
  const localKeys = useRef(new Map())
  const keys = operationKeys || localKeys.current
  const t = (zh, en) => lang === 'zh' ? zh : en
  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }))

  async function write(path, method, body) {
    if (lock.current) return
    lock.current = true
    setBusy(true); onBusyChange?.(true); setError('')
    try {
      await requestCommerceManagement(path, { method, body, idempotencyKey: operationKey(keys, path, method, body) })
      // Completed product operations may be repeated later, unlike ambiguous retries.
      keys.delete(JSON.stringify([path, method, body]))
      setForm(null)
      await onSuccess?.(t('商品已保存。', 'Product saved.'))
    } catch (cause) { setError(cause.message) }
    finally { lock.current = false; setBusy(false); onBusyChange?.(false) }
  }

  function save(event) {
    event.preventDefault()
    if (lock.current) return
    try {
      const body = productPayload(form)
      void write(editing ? `products/${encodeURIComponent(product.id)}` : 'products', editing ? 'PATCH' : 'POST', body)
    } catch (cause) { setError(cause.message) }
  }

  return <section className="space-y-3 rounded border border-slate-200 p-4 dark:border-slate-700" aria-label={t('商品操作', 'Product actions')}>
    <div className="flex flex-wrap gap-2">
      <button type="button" className={button} disabled={busy} onClick={() => { setEditing(false); setForm(productForm()); setError('') }}>{t('新增商品', 'Create product')}</button>
      {product?.id && <>
        <button type="button" className={button} disabled={busy} onClick={() => { setEditing(true); setForm(productForm(product)); setError('') }}>{t('编辑选中商品', 'Edit selected product')}</button>
        <button type="button" className={button} disabled={busy || Boolean(form)} onClick={() => void write(`products/${encodeURIComponent(product.id)}/status`, 'POST', { status: product.status === 'active' ? 'inactive' : 'active' })}>{product.status === 'active' ? t('下架商品', 'Deactivate product') : t('上架商品', 'Activate product')}</button>
      </>}
    </div>
    {error && <p role="alert" className="text-sm text-rose-600">{error}</p>}
    {busy && <p role="status">{t('保存中…', 'Saving…')}</p>}
    {form && <form onSubmit={save}>
      <fieldset disabled={busy} className="grid gap-3 text-sm sm:grid-cols-2">
        <legend className="mb-3 font-semibold">{editing ? t('编辑商品', 'Edit product') : t('新增商品（默认下架）', 'Create product (inactive by default)')}</legend>
        <label>{t('商品 ID', 'Product ID')}<input className={input} required pattern="[A-Za-z0-9][A-Za-z0-9._-]{0,127}" maxLength={128} disabled={editing || busy} value={form.id} onChange={(event) => update('id', event.target.value)} /></label>
        <label>SKU<input className={input} required maxLength={128} value={form.sku} onChange={(event) => update('sku', event.target.value)} /></label>
        <label>{t('名称', 'Name')}<input className={input} required maxLength={200} value={form.name} onChange={(event) => update('name', event.target.value)} /></label>
        <label>{t('价格（最小货币单位整数）', 'Price (integer minor units)')}<input className={input} required type="number" min="0" step="1" value={form.unitAmount} onChange={(event) => update('unitAmount', event.target.value)} /></label>
        <label>{t('货币', 'Currency')}<input className={input} required pattern="[A-Z]{3}" maxLength={3} value={form.currency} onChange={(event) => update('currency', event.target.value.toUpperCase())} /></label>
        <label>{t('类型', 'Kind')}<select className={input} value={form.kind} onChange={(event) => update('kind', event.target.value)}><option value="physical">{t('实体商品', 'Physical')}</option><option value="digital">{t('虚拟商品', 'Digital')}</option></select></label>
        <label>{t('状态', 'Status')}<select className={input} value={form.status} onChange={(event) => update('status', event.target.value)}><option value="inactive">{t('下架', 'Inactive')}</option><option value="active">{t('上架', 'Active')}</option></select></label>
        <label className="sm:col-span-2">{t('描述', 'Description')}<textarea className={input} rows={3} maxLength={4000} value={form.description} onChange={(event) => update('description', event.target.value)} /></label>
        <p className="sm:col-span-2 text-xs text-slate-500">{t('例如 USD 199 = $1.99；JPY 199 = ¥199。价格变更不影响已有订单。', 'For example, USD 199 = $1.99; JPY 199 = ¥199. Existing orders keep their original prices.')}</p>
        <div className="flex gap-2"><button className={button} type="submit">{t('保存商品', 'Save product')}</button><button className={button} type="button" onClick={() => { setForm(null); setError('') }}>{t('取消', 'Cancel')}</button></div>
      </fieldset>
    </form>}
  </section>
}
